import Pay, { CheckoutObject } from "@pay-com/js";
import { toast } from "react-toastify";
import ToastNotification from "../../../components/ToastNotification/toastNotification";
import { createPayInvoiceSession } from "../../subscription-checkout/subscription-checkout-service";
import { useEffect, useRef, useState } from "react";
import CloseModalIcon from "../../../shared-components/icons/CloseModalIcon";
import Skeleton from "react-loading-skeleton";
import { useSubscriptionContext } from "../subscriptionContext";
import ChevronsRightIcon from "../../../shared-components/icons/ChevronsRightIcon";
import { publishModalVisibility } from "../../../redux/features/modal/modalSlice";
import { useAppDispatch } from "../../../redux/hooks";
import { OnCloseReason } from "../interfaces";

const InvoicePaymentModal = ({
  invoiceId,
  onClose,
  onError,
}: {
  invoiceId: string;
  onClose?: (reason?: OnCloseReason) => void;
  onError?: (errorCode: "pay_iframe_error" | "create_session_error") => void;
}) => {
  const dispatch = useAppDispatch();
  const subscriptionContext = useSubscriptionContext();
  const setIsInvoiceProcessing = subscriptionContext?.setIsInvoiceProcessing;
  const [isPaymentCheckoutLoading, setIsPaymentCheckoutLoading] = useState(false);
  const paymentId = useRef<string>();
  const [isPaymentDone, setIsPaymentDone] = useState<boolean>(false);
  const payComRef = useRef<Awaited<ReturnType<typeof Pay.com>>>(null);
  const payComCheckout = useRef<CheckoutObject>(null);
  const [paySessionError, setPaySessionError] = useState<string>("");
  let userEmailString = "";
  const userEmailNode = document.querySelector("#userEmail");
  if (userEmailNode) {
    userEmailString = userEmailNode.textContent;
  }
  const [userEmail, setUserEmail] = useState<string>(userEmailString || "test@test.com");
  const [clientSecret, setClientSecret] = useState<string>();
  let merchantId = "";
  const merchantIdNode = document.querySelector("#merchant-Id");
  if (merchantIdNode) {
    merchantId = merchantIdNode.textContent;
  }
  let isLiveServer = false;
  const liveServerCheckNode = document.querySelector("#live-server-check");
  if (liveServerCheckNode) {
    isLiveServer = liveServerCheckNode.textContent === "True";
  }

  const handlePaySessionError = (error: any) => {
    let message = error.message.map((m: string) => m).join(",");
    setPaySessionError(message);
  };

  const removePayComContainers = () => {
    const payWalletsContainerDiv = document.querySelector("#_pay_wallets_container");
    if (payWalletsContainerDiv) {
      payWalletsContainerDiv.remove();
    }
    const payComIframe = document.querySelector("iframe");
    if (payComIframe) {
      payComIframe.remove();
    }
  };

  const initializePayComSDK = async (clientSecret: string) => {
    try {
      payComRef.current = await Pay.com({ identifier: merchantId, live: isLiveServer });
      payComCheckout.current = payComRef.current.checkout({
        clientSecret,
        onSuccess: (payment: any) => {
          removePayComContainers();
          setIsPaymentDone(true);
          dispatch(publishModalVisibility({ isOpen: false }));
        },
      });
      payComCheckout.current.on(payComCheckout.current.EVENT_TYPES.CONTENT_READY, () => {
        setIsPaymentCheckoutLoading(false);
      });
      await payComCheckout.current.universal({
        container: "#paycom_checkout",
        toggles: {
          submitButton: false,
        },
      });
    } catch (error) {
      if (onError) {
        onError("pay_iframe_error");
      }
      console.error(`Error while loading the iframe by Pay - ${JSON.stringify(error)}`);
    }
  };

  const createPayComIFrame = async (abortController: AbortController) => {
    try {
      setIsPaymentCheckoutLoading(true);
      const response = await createPayInvoiceSession(invoiceId, abortController);
      const data = await response.json();
      // if session can not be created by Pay.com
      if (data.statusCode !== undefined && data.statusCode !== 201) {
        handlePaySessionError(data);
      } else {
        initializePayComSDK(data.client_secret);
        setClientSecret(data.client_secret);
      }
    } catch (error) {
      if (error !== "Multiple rerenders, aborting") {
        toast.error(<ToastNotification message="Something went wrong. Please try again later." />);
        console.error(`Error while creating pay session - ${JSON.stringify(error)}`);
      }
      if (onError) {
        onError("create_session_error");
      }
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    createPayComIFrame(abortController);

    return () => {
      abortController.abort("Multiple rerenders, aborting");
    };
  }, []);

  useEffect(() => {
    document.querySelector("body").classList.add("overflow-hidden");
    return () => {
      document.querySelector("body").classList.remove("overflow-hidden");
    };
  }, []);

  const onPayClick = async () => {
    const validation = await payComCheckout.current.validate();

    if (validation && validation.valid) {
      try {
        await payComCheckout.current.submit();
        setIsInvoiceProcessing?.(true);
        _onClose("successSubmit");
      } catch (err) {
        toast.error(<ToastNotification message={`Checkout failed: ${err.message}`} />, {
          autoClose: 5000,
        });
      }
    } else {
      // Handle validation failure logic.
    }
  };

  const _onClose = (reason?: OnCloseReason) => {
    dispatch(publishModalVisibility({ isOpen: false }));
    if (onClose) {
      onClose(reason);
    }
  };

  return (
    <div>
      <div data-name="Overlay" className="fixed inset-0 z-[9997] bg-black/80" />
      <div
        data-name="PopupContainer"
        className={"fixed inset-0 z-[9998] w-full min-h-full overflow-x-hidden overflow-y-auto"}>
        <div className={"w-full min-h-full flex items-center justify-center flex-col m-auto"}>
          <div
            className={
              "bg-white relative w-full lg:max-w-[480px] px-4 py-6 lg:px-7.5 flex flex-col max-lg:grow lg:rounded-2xl"
            }>
            <button
              onClick={() => _onClose("closeButtonClicked")}
              className={"absolute flex items-center justify-center right-2.5 top-2.5 lg:right-3 lg:top-3"}>
              <CloseModalIcon className={"size-6 text-gray-5"} />
            </button>

            <div className="text-secondary font-jost text-base font-medium leading-5.5 lg:text-lg lg:leading-6 mb-6">
              Complete payment to keep enjoying your <br className={"lg:hidden"} /> BOTB Pass Membership
            </div>

            {isPaymentCheckoutLoading && (
              <div>
                <Skeleton
                  count={1}
                  inline
                  highlightColor="#fff"
                  containerClassName="flex flex-col w-full h-[488px] lg:h-[492px]"
                  className="h-full"
                />
              </div>
            )}
            <div
              id="paycom_checkout"
              className={`min-h-[448px] [&>iframe]:min-h-[448px] ${isPaymentCheckoutLoading ? "hidden" : "block"}`}
            />

            {!isPaymentCheckoutLoading && (
              <button
                className={"theme-btn theme-btn-primary theme-btn-3lg lg:theme-btn-2lg uppercase w-full"}
                disabled={isPaymentDone}
                onClick={onPayClick}>
                PAY <ChevronsRightIcon variant={2} className={"size-5 shrink-0"} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePaymentModal;
